import { useMemo } from 'react';
import { DevicePickerGrid } from '@/scheduling/components/shared/device-picker-grid';
import { technicianLine } from '@/scheduling/lib/calendar-mapping';
import type { BlockActor } from '@/types/block-availability-api';
import type { CalendarDevice } from '@/types/scheduling-api';

/**
 * BlockAvailabilityDeviceSearch — the DEVICE half of the block panel's actor
 * picker (PROD-2454), standing in the same slot as
 * `BlockAvailabilityProviderSearch` and built the same way: this file owns
 * the data and the projection to/from the form state's `BlockActor`, the
 * shared picker owns the cards, the search box and the loading / empty
 * states.
 *
 * Mirrors rather than overloads, per the standing 2026-09-13 ruling (share
 * the engine, not the endpoint): the provider search keeps its own feed, its
 * self-filter and its provider-worded copy, and this one keeps the device
 * registry and device-worded copy. What the two SHARE is `PickerGrid` and the
 * `BlockActor` contract they both hand back — which is the shape, and the
 * shape is the part that was worth sharing.
 *
 * The device list is NOT fetched here. It is passed in, already narrowed to
 * the blockable devices by `SchedulingPage` (`filterBlockableDevices`), for
 * the same reason the Book-a-device rail takes its list rather than fetching
 * it: the narrowing needs BOTH the registry and the slots feed, only the page
 * holds both, and a second copy of that join is a second place for the two
 * surfaces to disagree about which machines exist.
 */

interface Props {
  /**
   * The blockable devices of the practice — ordered and narrowed by the
   * caller (`filterBlockableDevices`). Never the raw registry: a device with
   * no working hours has no schedule to block.
   */
  devices: readonly CalendarDevice[];
  /** The selected device projected onto the form state's actor slot. */
  selectedDevice: BlockActor | null;
  onSelect: (actor: BlockActor) => void;
  onClear: () => void;
  isLoading?: boolean;
  /**
   * Read-only mode (edit view) — same contract as the provider search's
   * `disabled`: the selected card stays, the × and the grid go, because an
   * existing block's actor is not something the panel lets you change.
   */
  disabled?: boolean;
}

/**
 * Registry row → the form state's actor slot.
 *
 * File-local, exactly like the provider search's `toActor`: it is this
 * adapter's own projection, nothing outside needs it, and exporting it would
 * make this a mixed component/util module — which `react-refresh` objects to,
 * and which the sibling deliberately is not.
 */
function deviceToActor(d: CalendarDevice): BlockActor {
  return {
    id: d.id,
    name: d.name,
    // Devices have no avatar; the card renders initials from the name.
    photo: null,
    // The SAME second line the Devices strip, the column header and the
    // Book-a-device card use, so an unassigned machine reads
    // "No technician assigned" here too rather than a fourth wording.
    subtitle: technicianLine(d.technicianName),
  };
}

export function BlockAvailabilityDeviceSearch({
  devices,
  selectedDevice,
  onSelect,
  onClear,
  isLoading = false,
  disabled = false,
}: Props) {
  /**
   * `DevicePickerGrid` renders a `CalendarDevice`, and the form state holds a
   * `BlockActor`, so the selected card has to be resolved back.
   *
   * Prefer the REGISTRY row for the same id over reconstructing one from the
   * actor: in edit mode the actor came from
   * `/api/Appointment/BlockAppointment`, which returns the block's actor
   * through a practitioner-shaped slot and so carries no room, no technician
   * and — when the backend fills only the id — no name either. The registry
   * is what owns that identity everywhere else on this tab.
   *
   * The fallback is not dead code: a machine ARCHIVED after the block was
   * created is gone from `devices`, and an edit panel that rendered no actor
   * card at all would read as "this block belongs to nobody".
   */
  const selectedRow = useMemo<CalendarDevice | null>(() => {
    if (!selectedDevice) return null;
    const known = devices.find((d) => d.id === selectedDevice.id);
    if (known) return known;
    return {
      id: selectedDevice.id,
      name: selectedDevice.name,
      type: '',
      practiceId: '',
      status: 'active',
      technicianName: null,
    };
  }, [devices, selectedDevice]);

  return (
    <DevicePickerGrid
      devices={devices}
      selected={selectedRow}
      isLoading={isLoading}
      onSelect={(d) => onSelect(deviceToActor(d))}
      onClear={onClear}
      readOnly={disabled}
      // The Book-a-device wording names the products a BOOKING needs. A block
      // needs none of that — only working hours — so the empty state has to
      // say the one thing that is actually missing, or it sends the user to
      // configure a product that would not have helped (`isBlockableDevice`).
      emptyText="No blockable devices — a device needs working hours under Settings › Devices › Schedule."
    />
  );
}
